import React, { useEffect, useState, useMemo } from "react";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaThList,
  FaThLarge,
  FaEye,
  FaUsers,
  FaEdit,
  FaTrash,
  FaExclamationTriangle,
} from "react-icons/fa";

interface Installment {
  id: number;
  sequenceNumber: number;
  dueDate: string;
  amount: number;
  paid: boolean;
  paidDate?: string;
  status: "PAID" | "UPCOMING" | "UNPAID" | "IN_REVIEW";
  inReview: boolean;
  reviewRequestDate?: string;
}

interface Enrollment {
  batchName: any;
  id: number;
  studentFirstName: string;
  studentLastName: string;
  studentEmail: string;
  courseTitle: string;
  enrollmentDate: string;
  status: string;
  installments: Installment[];
  discountPercentage?: number;
  discountAppliedDate?: string;
  discountAppliedBy?: string;
  originalAmount?: number;
  discountedAmount?: number;
  refundAmount?: number;
  refundProcessed?: boolean;
  refundProcessedDate?: string;
}

interface ReportStats {
  totalStudents: number;
  totalEnrollments: number;
  germanEnrollments: number;
  appointmentEnrollments: number;
}

// Modal Component
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  showIcon?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type,
  showIcon = true,
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "success":
        return <FaCheckCircle className="text-green-500" size={48} />;
      case "error":
        return <FaTimesCircle className="text-red-500" size={48} />;
      case "warning":
        return <FaExclamationTriangle className="text-orange-500" size={48} />;
      case "info":
        return <FaEye className="text-blue-500" size={48} />;
      default:
        return null;
    }
  };

  const getColors = () => {
    switch (type) {
      case "success":
        return "bg-green-50 border-green-200";
      case "error":
        return "bg-red-50 border-red-200";
      case "warning":
        return "bg-orange-50 border-orange-200";
      case "info":
        return "bg-blue-50 border-blue-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case "success":
        return "bg-green-600 hover:bg-green-700";
      case "error":
        return "bg-red-600 hover:bg-red-700";
      case "warning":
        return "bg-orange-600 hover:bg-orange-700";
      case "info":
        return "bg-blue-600 hover:bg-blue-700";
      default:
        return "bg-gray-600 hover:bg-gray-700";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-fade-in">
        <div className={`p-6 rounded-t-2xl border-b-2 ${getColors()}`}>
          <div className="flex flex-col items-center text-center">
            {showIcon && <div className="mb-4">{getIcon()}</div>}
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          </div>
        </div>
        <div className="p-6">
          <p className="text-gray-700 text-center whitespace-pre-line leading-relaxed">
            {message}
          </p>
        </div>
        <div className="p-6 pt-0 flex justify-center">
          <button
            onClick={onClose}
            className={`px-8 py-3 rounded-lg text-white font-semibold transition-colors ${getButtonColor()}`}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Confirmation Dialog Component
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: "danger" | "warning" | "info";
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning",
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "danger":
        return <FaTimesCircle className="text-red-500" size={48} />;
      case "warning":
        return <FaExclamationTriangle className="text-orange-500" size={48} />;
      case "info":
        return <FaEye className="text-blue-500" size={48} />;
      default:
        return <FaExclamationTriangle className="text-orange-500" size={48} />;
    }
  };

  const getConfirmButtonColor = () => {
    switch (type) {
      case "danger":
        return "bg-red-600 hover:bg-red-700";
      case "warning":
        return "bg-orange-600 hover:bg-orange-700";
      case "info":
        return "bg-blue-600 hover:bg-blue-700";
      default:
        return "bg-orange-600 hover:bg-orange-700";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-fade-in">
        <div className="p-6">
          <div className="flex flex-col items-center text-center mb-4">
            <div className="mb-4">{getIcon()}</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
            <p className="text-gray-700 whitespace-pre-line leading-relaxed">
              {message}
            </p>
          </div>
        </div>
        <div className="p-6 pt-0 flex gap-3 justify-center">
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`px-6 py-3 rounded-lg text-white font-semibold transition-colors ${getConfirmButtonColor()}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminEnrollments: React.FC = () => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [reportStats, setReportStats] = useState<ReportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const [view, setView] = useState<"table" | "card">("table");

  const [filterCourse, setFilterCourse] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterInstallment, setFilterInstallment] = useState("");
  const [filterBatch, setFilterBatch] = useState("");

  const [editingDiscountId, setEditingDiscountId] = useState<number | null>(
    null
  );
  const [discountValue, setDiscountValue] = useState<string>("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  // Modal states
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: "danger" | "warning" | "info";
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "warning",
  });

  // Helper functions to show modal/dialog
  const showModal = (
    title: string,
    message: string,
    type: "success" | "error" | "warning" | "info"
  ) => {
    setModal({ isOpen: true, title, message, type });
  };

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    type: "danger" | "warning" | "info" = "warning"
  ) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm, type });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const batchList = useMemo(
    () =>
      Array.from(
        new Set(
          enrollments
            .map((e) => e.batchName)
            .filter((batch) => batch && batch !== "")
        )
      ).sort(),
    [enrollments]
  );

  const courseList = useMemo(
    () => Array.from(new Set(enrollments.map((e) => e.courseTitle))).sort(),
    [enrollments]
  );

  const statusList = useMemo(
    () => Array.from(new Set(enrollments.map((e) => e.status))).sort(),
    [enrollments]
  );

  const courseStats = useMemo(() => {
    const stats: Record<string, number> = {};
    enrollments.forEach((e) => {
      stats[e.courseTitle] = (stats[e.courseTitle] || 0) + 1;
    });
    return Object.entries(stats).sort((a, b) => b[1] - a[1]);
  }, [enrollments]);

  const filteredEnrollments = useMemo(() => {
    return enrollments.filter((e) => {
      if (filterCourse && e.courseTitle !== filterCourse) return false;
      if (filterStatus && e.status !== filterStatus) return false;
      if (filterBatch) {
        if (filterBatch === "no-batch") {
          if (e.batchName && e.batchName !== "") return false;
        } else {
          if (e.batchName !== filterBatch) return false;
        }
      }
      if (filterInstallment) {
        const hasStatus = e.installments.some(
          (i) => i.status === filterInstallment.toUpperCase()
        );
        if (!hasStatus) return false;
      }
      return true;
    });
  }, [enrollments, filterCourse, filterStatus, filterBatch, filterInstallment]);

  const getInstallmentStatusDisplay = (installment: Installment) => {
    switch (installment.status) {
      case "PAID":
        return (
          <span className="flex items-center text-green-600">
            <FaCheckCircle className="mr-1" /> Paid
            {installment.paidDate
              ? ` (${new Date(installment.paidDate).toLocaleDateString()})`
              : ""}
          </span>
        );
      case "IN_REVIEW":
        return (
          <span className="flex items-center text-orange-600">
            <FaEye className="mr-1" /> Under Review
            {installment.reviewRequestDate
              ? ` (${new Date(
                  installment.reviewRequestDate
                ).toLocaleDateString()})`
              : ""}
          </span>
        );
      case "UPCOMING":
        return (
          <span className="flex items-center text-blue-600">
            <FaClock className="mr-1" /> Upcoming
          </span>
        );
      case "UNPAID":
        return (
          <span className="flex items-center text-red-500">
            <FaTimesCircle className="mr-1" /> Overdue
          </span>
        );
      default:
        return (
          <span className="flex items-center text-gray-500">
            <FaTimesCircle className="mr-1" /> Unknown
          </span>
        );
    }
  };

  const getInstallmentStatusBadge = (installment: Installment) => {
    const baseClasses =
      "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold";

    switch (installment.status) {
      case "PAID":
        return (
          <span className={`${baseClasses} text-green-700 bg-green-50`}>
            <FaCheckCircle className="text-green-500" />
            Paid
            {installment.paidDate
              ? ` (${new Date(installment.paidDate).toLocaleDateString()})`
              : ""}
          </span>
        );
      case "IN_REVIEW":
        return (
          <span className={`${baseClasses} text-orange-700 bg-orange-50`}>
            <FaEye className="text-orange-500" />
            Under Review
            {installment.reviewRequestDate
              ? ` (${new Date(
                  installment.reviewRequestDate
                ).toLocaleDateString()})`
              : ""}
          </span>
        );
      case "UPCOMING":
        return (
          <span className={`${baseClasses} text-blue-700 bg-blue-50`}>
            <FaClock className="text-blue-500" />
            Upcoming
          </span>
        );
      case "UNPAID":
        return (
          <span className={`${baseClasses} text-red-600 bg-red-50`}>
            <FaTimesCircle className="text-red-500" />
            Unpaid
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} text-gray-600 bg-gray-50`}>
            <FaTimesCircle className="text-gray-500" />
            Unknown
          </span>
        );
    }
  };

  const applyDiscount = async (
    enrollmentId: number,
    discountPercentage: number
  ) => {
    try {
      setApplyingDiscount(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/enrollments/${enrollmentId}/discount`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ discountPercentage }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to apply discount");
      }

      const result = await response.json();

      let message = `Original: ${result.discount.originalAmount} BDT\n`;
      message += `Discounted: ${result.discount.discountedAmount} BDT\n`;
      message += `Savings: ${(
        result.discount.originalAmount - result.discount.discountedAmount
      ).toFixed(2)} BDT`;

      if (result.discount.refundDue) {
        message += `\n\n⚠️ REFUND DUE: ${result.discount.refundAmount.toFixed(
          2
        )} BDT\n\n`;
        message += `All installments are already paid.\nPlease process refund manually via bank/bKash,\nthen click "Mark Paid" to complete.`;
      }

      showModal("Discount Applied Successfully!", message, "success");
      await refreshData();
    } catch (err: any) {
      showModal("Failed to Apply Discount", err.message, "error");
    } finally {
      setApplyingDiscount(false);
      setEditingDiscountId(null);
      setDiscountValue("");
    }
  };

  const removeDiscount = (enrollmentId: number) => {
    showConfirmDialog(
      "Remove Discount",
      "Are you sure you want to remove this discount?\n\nThis will recalculate unpaid installments back to original amounts.",
      async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/admin/enrollments/${enrollmentId}/discount`,
            {
              method: "DELETE",
              credentials: "include",
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to remove discount");
          }

          showModal(
            "Discount Removed",
            "The discount has been successfully removed.",
            "success"
          );
          await refreshData();
        } catch (err: any) {
          showModal("Failed to Remove Discount", err.message, "error");
        }
      },
      "danger"
    );
  };

  const markRefundProcessed = (enrollmentId: number) => {
    showConfirmDialog(
      "Confirm Refund Processed",
      "Have you processed this refund outside the system?\n\nThis action will mark the refund as complete.",
      async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/admin/enrollments/${enrollmentId}/discount/refund-processed`,
            {
              method: "POST",
              credentials: "include",
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              errorData.error || "Failed to mark refund as processed"
            );
          }

          showModal(
            "Refund Marked as Processed",
            "The refund has been successfully recorded.",
            "success"
          );
          await refreshData();
        } catch (err: any) {
          showModal("Failed to Mark Refund", err.message, "error");
        }
      },
      "info"
    );
  };

  const fetchData = async () => {
    try {
      const [enrollmentRes, reportRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/enrollments`, {
          credentials: "include",
        }),
        fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/enrollments/reports`,
          {
            credentials: "include",
          }
        ),
      ]);
      if (!enrollmentRes.ok || !reportRes.ok) {
        throw new Error("Failed to fetch data");
      }
      const enrollmentData = await enrollmentRes.json();
      const reportData = await reportRes.json();
      setEnrollments(
        Array.isArray(enrollmentData)
          ? enrollmentData
          : enrollmentData.enrollments || []
      );
      setReportStats(reportData);
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Modals */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />

      {/* HEADER */}
      <PageHeader
        icon={<FaUsers />}
        title="Manage Enrollments"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Enrollments" },
        ]}
      />

      {/* CONTENT */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <p className="mb-8 text-gray-700" style={{ fontSize: "16px" }}>
          Welcome to the{" "}
          <span className="font-semibold text-[#009fe3]">
            Guideline2Germany
          </span>{" "}
          enrollment management panel. Filter or browse all student enrollments
          below.
        </p>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {reportStats && (
            <>
              <div className="bg-white shadow rounded-lg p-4 text-center">
                <p className="text-gray-600 text-xs font-semibold uppercase mb-1">
                  Total Students
                </p>
                <p className="text-xl font-bold text-[#009fe3]">
                  {reportStats.totalStudents}
                </p>
              </div>
              <div className="bg-white shadow rounded-lg p-4 text-center">
                <p className="text-gray-600 text-xs font-semibold uppercase mb-1">
                  Total Enrollments
                </p>
                <p className="text-xl font-bold text-[#009fe3]">
                  {reportStats.totalEnrollments}
                </p>
              </div>
            </>
          )}

          {courseStats.map(([course, count], idx) => (
            <div
              key={idx}
              className="bg-white shadow rounded-lg p-4 text-center"
              title={course}
            >
              <p className="text-gray-600 text-xs font-semibold uppercase mb-1">
                {course.length > 20 ? `${course.substring(0, 20)}...` : course}
              </p>
              <p className="text-xl font-bold text-[#009fe3]">{count}</p>
            </div>
          ))}
        </div>

        {/* View Toggle and Filters */}
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setView("table")}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 transition ${
                view === "table"
                  ? "bg-[#009fe3] text-white shadow"
                  : "bg-white text-[#009fe3] border border-[#009fe3] hover:bg-[#e6f7fa]"
              }`}
            >
              <FaThList />
              Table
            </button>
            <button
              onClick={() => setView("card")}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 transition ${
                view === "card"
                  ? "bg-[#009fe3] text-white shadow"
                  : "bg-white text-[#009fe3] border border-[#009fe3] hover:bg-[#e6f7fa]"
              }`}
            >
              <FaThLarge />
              Card
            </button>
          </div>
          <div className="flex flex-wrap gap-4 ml-auto">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Course</label>
              <select
                className="border rounded px-2 py-1"
                value={filterCourse}
                onChange={(e) => setFilterCourse(e.target.value)}
                style={{ fontSize: "16px" }}
              >
                <option value="">All</option>
                {courseList.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="border rounded px-2 py-1"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ fontSize: "16px" }}
              >
                <option value="">All</option>
                {statusList.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Batch</label>
              <select
                className="border rounded px-2 py-1"
                value={filterBatch}
                onChange={(e) => setFilterBatch(e.target.value)}
                style={{ fontSize: "16px" }}
              >
                <option value="">All</option>
                {batchList.map((batch) => (
                  <option key={batch} value={batch}>
                    {batch}
                  </option>
                ))}
                <option value="no-batch">No Batch</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Payment Status
              </label>
              <select
                className="border rounded px-2 py-1"
                value={filterInstallment}
                onChange={(e) => setFilterInstallment(e.target.value)}
                style={{ fontSize: "16px" }}
              >
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="in_review">Under Review</option>
                <option value="upcoming">Upcoming</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            {(filterCourse ||
              filterStatus ||
              filterBatch ||
              filterInstallment) && (
              <button
                className="text-xs text-gray-500 underline"
                onClick={() => {
                  setFilterCourse("");
                  setFilterStatus("");
                  setFilterBatch("");
                  setFilterInstallment("");
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <p className="text-center py-10">Loading enrollments…</p>
        ) : error ? (
          <p className="text-red-600 text-center py-10">{error}</p>
        ) : filteredEnrollments.length === 0 ? (
          <p className="text-center text-gray-600 py-10">
            No enrollments found.
          </p>
        ) : view === "table" ? (
          <div className="bg-white shadow rounded-lg overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-[#009fe3] text-white">
                <tr>
                  <th className="px-6 py-3 text-sm font-medium uppercase text-left">
                    Student
                  </th>
                  <th className="px-6 py-3 text-sm font-medium uppercase text-left">
                    Email
                  </th>
                  <th className="px-6 py-3 text-sm font-medium uppercase text-left">
                    Course
                  </th>
                  <th className="px-6 py-3 text-sm font-medium uppercase text-left">
                    Batch
                  </th>
                  <th className="px-6 py-3 text-sm font-medium uppercase text-left">
                    Enrolled
                  </th>
                  <th className="px-6 py-3 text-sm font-medium uppercase text-left">
                    Status
                  </th>
                  <th className="px-6 py-3 text-sm font-medium uppercase text-left">
                    Discount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredEnrollments.map((enrollment) => (
                  <tr
                    key={enrollment.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {enrollment.studentFirstName} {enrollment.studentLastName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {enrollment.studentEmail}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {enrollment.courseTitle}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {enrollment.batchName || (
                        <span className="text-gray-400 text-xs">No batch</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {new Date(enrollment.enrollmentDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          enrollment.status === "ACTIVE"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {enrollment.status}
                      </span>
                    </td>
                    {/* ✅ FIXED: Discount Cell */}
                    <td className="px-6 py-4 text-sm">
                      {editingDiscountId === enrollment.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={discountValue}
                            onChange={(e) => setDiscountValue(e.target.value)}
                            className="w-20 px-2 py-1 border rounded"
                            placeholder="%"
                            autoFocus
                          />
                          <button
                            onClick={() => {
                              const percent = parseFloat(discountValue);
                              if (
                                isNaN(percent) ||
                                percent < 0 ||
                                percent > 100
                              ) {
                                showModal(
                                  "Invalid Input",
                                  "Please enter a valid percentage between 0 and 100.",
                                  "error"
                                );
                                return;
                              }

                              showConfirmDialog(
                                "Apply Discount",
                                `Are you sure you want to apply ${percent}% discount to this enrollment?\n\nThis will recalculate unpaid installments.`,
                                () => applyDiscount(enrollment.id, percent),
                                "info"
                              );
                            }}
                            disabled={applyingDiscount}
                            className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => {
                              setEditingDiscountId(null);
                              setDiscountValue("");
                            }}
                            className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                          >
                            ✕
                          </button>
                        </div>
                      ) : enrollment.discountPercentage &&
                        enrollment.discountPercentage > 0 ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span
                              className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full cursor-help"
                              title={`Applied by ${
                                enrollment.discountAppliedBy
                              } on ${
                                enrollment.discountAppliedDate
                                  ? new Date(
                                      enrollment.discountAppliedDate
                                    ).toLocaleDateString()
                                  : "N/A"
                              }\nOriginal: ${
                                enrollment.originalAmount
                              } BDT\nDiscounted: ${
                                enrollment.discountedAmount
                              } BDT`}
                            >
                              {enrollment.discountPercentage}% OFF
                            </span>
                            <button
                              onClick={() => {
                                setEditingDiscountId(enrollment.id);
                                setDiscountValue(
                                  enrollment.discountPercentage?.toString() ||
                                    ""
                                );
                              }}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit discount"
                            >
                              <FaEdit size={14} />
                            </button>
                            <button
                              onClick={() => removeDiscount(enrollment.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Remove discount"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                          {/* ✅ TRIPLE CHECK: Explicit null/undefined/zero check */}
                          {enrollment.refundAmount !== null &&
                            enrollment.refundAmount !== undefined &&
                            enrollment.refundAmount > 0 && (
                              <div className="flex items-center gap-2">
                                {enrollment.refundProcessed ? (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                                    ✓ Refunded{" "}
                                    {enrollment.refundAmount.toFixed(2)} BDT
                                    {enrollment.refundProcessedDate &&
                                      ` on ${new Date(
                                        enrollment.refundProcessedDate
                                      ).toLocaleDateString()}`}
                                  </span>
                                ) : (
                                  <>
                                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full animate-pulse">
                                      ⚠️ Refund Due:{" "}
                                      {enrollment.refundAmount.toFixed(2)} BDT
                                    </span>
                                    <button
                                      onClick={() =>
                                        markRefundProcessed(enrollment.id)
                                      }
                                      className="px-2 py-1 bg-orange-500 text-white text-xs rounded hover:bg-orange-600"
                                      title="Mark refund as processed"
                                    >
                                      Mark Paid
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingDiscountId(enrollment.id);
                            setDiscountValue("");
                          }}
                          className="px-3 py-1 bg-[#009fe3] text-white text-xs rounded hover:bg-[#007cb5]"
                        >
                          + Add
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          // Card view
          <div className="grid gap-6 md:grid-cols-2">
            {filteredEnrollments.map((enrollment) => (
              <div
                key={enrollment.id}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-[#e6f0fa]"
              >
                <div className="font-semibold text-lg text-gray-800 mb-1">
                  {enrollment.studentFirstName} {enrollment.studentLastName}
                </div>
                <div className="text-sm text-gray-500 mb-2">
                  {enrollment.studentEmail}
                </div>
                <div className="font-medium text-gray-700 mb-1">
                  {enrollment.courseTitle}
                </div>
                <div className="flex flex-wrap gap-2 text-xs mb-3">
                  <span className="bg-[#e6f7fa] text-[#009fe3] px-2 py-1 rounded-full font-medium">
                    Enrolled:{" "}
                    {new Date(enrollment.enrollmentDate).toLocaleDateString()}
                  </span>
                  <span
                    className={`px-2 py-1 rounded-full font-medium ${
                      enrollment.status === "ACTIVE"
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {enrollment.status}
                  </span>
                  {enrollment.batchName && (
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                      {enrollment.batchName}
                    </span>
                  )}
                  {/* ✅ FIXED: Discount Badge in Card */}
                  {enrollment.discountPercentage &&
                    enrollment.discountPercentage > 0 && (
                      <div className="flex flex-col gap-1 w-full">
                        <span
                          className="bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium text-xs cursor-help"
                          title={`Applied by ${
                            enrollment.discountAppliedBy
                          } on ${
                            enrollment.discountAppliedDate
                              ? new Date(
                                  enrollment.discountAppliedDate
                                ).toLocaleDateString()
                              : "N/A"
                          }\nOriginal: ${
                            enrollment.originalAmount
                          } BDT\nDiscounted: ${
                            enrollment.discountedAmount
                          } BDT`}
                        >
                          {enrollment.discountPercentage}% OFF
                        </span>
                        {/* ✅ TRIPLE CHECK: Explicit null/undefined/zero check */}
                        {enrollment.refundAmount !== null &&
                          enrollment.refundAmount !== undefined &&
                          enrollment.refundAmount > 0 &&
                          (enrollment.refundProcessed ? (
                            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium text-xs">
                              ✓ Refunded {enrollment.refundAmount.toFixed(2)}{" "}
                              BDT
                            </span>
                          ) : (
                            <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium text-xs animate-pulse">
                              ⚠️ Refund Due:{" "}
                              {enrollment.refundAmount.toFixed(2)} BDT
                            </span>
                          ))}
                      </div>
                    )}
                </div>
                <div>
                  <div className="font-semibold text-gray-700 mb-1">
                    Installments
                  </div>
                  <div className="flex flex-col gap-1">
                    {enrollment.installments?.map((inst) => (
                      <div
                        key={inst.id}
                        className="flex flex-wrap md:flex-nowrap items-center gap-2 bg-[#f9fbfd] rounded-lg px-2 py-1"
                      >
                        <span className="font-semibold text-gray-600">
                          #{inst.sequenceNumber}
                        </span>
                        <span className="text-gray-500">
                          Due: {new Date(inst.dueDate).toLocaleDateString()}
                        </span>
                        <span className="text-gray-700 font-medium">
                          {inst.amount} BDT
                        </span>
                        {getInstallmentStatusBadge(inst)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* CSS animation */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AdminEnrollments;
